import React from "react";
import styled from "styled-components";

const PageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-light);
  font-style: italic;
`;

const Content = styled.div`
  h2 {
    color: var(--text-dark);
    margin: 40px 0 20px 0;
    font-family: "Phudu", sans-serif;
  }

  h3 {
    color: var(--text-dark);
    margin: 30px 0 15px 0;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
  }

  ul,
  ol {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: var(--text-dark);
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  margin-top: 40px;
  text-align: center;

  h3 {
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 10px;
  }

  a {
    color: var(--secondary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsConditions = () => {
  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Terms & Conditions</PageTitle>
          <LastUpdated>Last updated: October 14, 2025</LastUpdated>
        </PageHeader>

        <Content>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using the services provided by Baypoint Storage
            ("Company", "we", "us"), you agree to be bound by these Terms and
            Conditions ("Terms"). If you do not agree to these Terms, please do
            not use our services.
          </p>

          <h2>2. Services</h2>
          <p>
            Baypoint Storage provides shipping container sales, rentals,
            modifications, and related services. We reserve the right to modify,
            suspend, or discontinue any service at any time without notice.
          </p>

          <h3>Container Sales</h3>
          <p>
            All container sales are final upon delivery and acceptance.
            Containers are sold "as-is" with warranties as explicitly stated in
            your purchase agreement.
          </p>

          <h3>Container Rentals</h3>
          <p>
            Rental terms, including duration, rates, and conditions, are
            specified in individual rental agreements. Rental fees are due as
            specified in the rental agreement.
          </p>

          <h2>3. Pricing and Payment</h2>
          <ul>
            <li>
              All prices are subject to change without notice until confirmed in
              writing
            </li>
            <li>Payment terms are specified in individual contracts</li>
            <li>
              Late payments may incur additional fees as specified in agreements
            </li>
            <li>All sales are subject to applicable taxes</li>
          </ul>

          <h2>4. Delivery and Risk of Loss</h2>
          <p>
            Delivery terms are specified in individual agreements. Risk of loss
            transfers to the customer upon delivery and placement of the
            container at the specified location.
          </p>

          <h3>Delivery Requirements</h3>
          <ul>
            <li>Customer must provide suitable access and placement area</li>
            <li>
              Site must be level, stable, and accessible to delivery vehicles
            </li>
            <li>Customer is responsible for obtaining necessary permits</li>
            <li>
              Delivery appointments are estimates and may vary due to weather or
              other conditions
            </li>
          </ul>

          <h2>5. Customer Responsibilities</h2>
          <ul>
            <li>Provide accurate delivery information and site access</li>
            <li>Ensure containers are used only for lawful purposes</li>
            <li>Maintain containers in good condition during rental periods</li>
            <li>
              Obtain all necessary permits and approvals for container placement
            </li>
            <li>Comply with all local zoning and building codes</li>
          </ul>

          <h2>6. Prohibited Uses</h2>
          <p>Containers may not be used for:</p>
          <ul>
            <li>Storage of hazardous, toxic, or illegal materials</li>
            <li>Human habitation without proper permits and modifications</li>
            <li>Activities that violate local laws or regulations</li>
            <li>
              Storage of items that could damage the container or create
              liability
            </li>
          </ul>

          <h2>7. Warranties and Disclaimers</h2>
          <p>
            <strong>Limited Warranty:</strong> We warrant that containers will
            be as described and in the condition stated at the time of delivery.
            This warranty is limited to repair or replacement of defective
            containers.
          </p>
          <p>
            <strong>Disclaimer:</strong> EXCEPT AS EXPRESSLY SET FORTH HEREIN,
            WE MAKE NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WITHOUT
            LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, OR NON-INFRINGEMENT.
          </p>

          <h2>8. Limitation of Liability</h2>
          <p>
            IN NO EVENT SHALL BAYPOINT STORAGE BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
            WITHOUT LIMITATION LOST PROFITS, DATA, OR GOODWILL, ARISING OUT OF
            OR RELATING TO THESE TERMS OR OUR SERVICES.
          </p>

          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Baypoint Storage from and
            against any claims, damages, losses, or expenses arising from your
            use of our containers or services, including your violation of these
            Terms.
          </p>

          <h2>10. Force Majeure</h2>
          <p>
            We shall not be liable for any delay or failure to perform due to
            circumstances beyond our reasonable control, including acts of God,
            natural disasters, war, terrorism, labor disputes, or government
            regulations.
          </p>

          <h2>11. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of the State of Indiana, without regard to its conflict of
            law provisions.
          </p>

          <h2>12. Dispute Resolution</h2>
          <p>
            Any disputes arising under these Terms shall be resolved through
            binding arbitration in accordance with the rules of the American
            Arbitration Association, conducted in Porter County, Indiana.
          </p>

          <h2>13. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. Changes will
            be effective upon posting to our website. Your continued use of our
            services constitutes acceptance of modified Terms.
          </p>

          <h2>14. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable, the
            remaining provisions shall remain in full force and effect.
          </p>

          <ContactInfo>
            <h3>Contact Us</h3>
            <p>
              If you have any questions about these Terms & Conditions, please
              contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:legal@baypointstorage.com">
                legal@baypointstorage.com
              </a>
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:+14845429203">(484) 542-9203</a>
            </p>
            <p>
              <strong>Address:</strong> Baypoint Storage, Portage, Indiana,
              United States
            </p>
          </ContactInfo>
        </Content>
      </Container>
    </PageWrapper>
  );
};

export default TermsConditions;
